import React, { useMemo } from 'react'
import { formatPrice } from 'services/plans'
import Button, { TYPES as BUTTON_TYPES } from 'components/Button'
import CountdownTimer, { FORMAT_HH_MM_SS, FORMAT_DAYS_HRS_MINS } from 'components/CountdownTimer'

export const OFFER_DISPLAY_TYPE_CARD = 'card'
export const OFFER_DISPLAY_TYPE_PROMO = 'promo'

/**
 * The special offer card view component
 * @param {Map} props.staticText - The static text
 * @param {Object} props.billingData - The biling plan data
 * @param {Object} props.offerLink - The offer link url and query data
 * @param {Number} props.durationMs - The offer timer value in ms
 * @param {Function} props.onOfferClick - The offer button click handler
 * @returns {JSX.Element} special offer component
 */
function OfferCard (props) {
  const {
    staticText,
    billingData,
    offerLink = {},
    durationMs,
    onOfferClick,
  } = props

  return (
    <div className="share-page-v3__offer-card">
      <div className="share-page-v3__offer-card-header">
        {staticText.get('offerLabel')}
      </div>
      <div className="share-page-v3__offer-card-content">
        <p className="share-page-v3__offer-card-content-title1">
          {staticText.get('offerTitleSmallLine1')}
        </p>
        <p className="share-page-v3__offer-card-content-title2">
          {staticText.get('offerTitleSmallLine2')}
        </p>
        <p className="share-page-v3__offer-card-content-text">
          {staticText.get('offerTextPart1')} <span>{staticText.get('offerTextPart2')}</span> {staticText.get('offerTextPart3')}
        </p>
        <p className="share-page-v3__offer-card-content-price">
          {billingData.offerPrice
            ? <>
              <span>{billingData.monthlyPrice}</span>
              <b>{` ${billingData.offerPrice} `}</b>
              {staticText.get('offerPrice').replace(/\$\{ currencyIso \}/, '')}
            </>
            : null
          }
        </p>
        <Button
          className="share-page-v3__offer-card-button"
          type={BUTTON_TYPES.PRIMARY}
          onClick={onOfferClick}
          {...offerLink}
        >
          {staticText.get('claimMySpecialOffer')}
        </Button>
        {billingData.firstPayment
          ? <p className="share-page-v3__offer-card-content-billing">
            {staticText.get('billedToday').replace(/\$\{ currencyIso \}/, billingData.firstPayment)}
          </p>
          : null
        }
      </div>
      <div className="share-page-v3__offer-card-footer">
        <p className="share-page-v3__offer-card-footer-title">
          {staticText.get('limitedOffer')}
        </p>
        <p className="share-page-v3__offer-card-footer-text">
          {staticText.get('expiresIn').replace(/\$\{ time \}/, '')}
          <CountdownTimer durationMs={durationMs} format={FORMAT_HH_MM_SS}/>
        </p>
      </div>
    </div>
  )
}

/**
 * The special offer promo view component
 * @param {Map} props.staticText - The static text
 * @param {Object} props.billingData - The biling plan data
 * @param {Object} props.offerLink - The offer link url and query data
 * @param {Number} props.durationMs - The offer timer value in ms
 * @param {Function} props.onOfferClick - The offer button click handler
 * @returns {JSX.Element} special offer component
 */
function OfferPromo (props) {
  const {
    staticText,
    billingData,
    offerLink = {},
    durationMs,
    onOfferClick,
    language,
  } = props

  const promoClassName = [
    'share-page-v3__offer-promo',
    `share-page-v3__offer-promo--${language.toLowerCase()}`,
  ].join(' ')

  return (
    <div className={promoClassName}>
      <div className="share-page-v3__offer-promo-label">
        {staticText.get('offerLabel')}
      </div>
      <p className="share-page-v3__offer-promo-title">
        {staticText.get('offerTitleLarge')}
      </p>
      <p className="share-page-v3__offer-promo-text">
        {staticText.get('offerTextPart1')} <span>{staticText.get('offerTextPart2')}</span> {staticText.get('offerTextPart3')}
      </p>
      <div className="share-page-v3__offer-promo-cta">
        <Button
          className="share-page-v3__offer-promo-button"
          type={BUTTON_TYPES.PRIMARY}
          onClick={onOfferClick}
          {...offerLink}
        >
          {staticText.get('claimMySpecialOffer')}
        </Button>
        <div className="share-page-v3__offer-promo-billing">
          <p className="share-page-v3__offer-promo-price">
            {billingData.offerPrice
              ? <>
                <span>{billingData.monthlyPrice}</span>
                <b>{` ${billingData.offerPrice} `}</b>
                {staticText.get('offerPrice').replace(/\$\{ currencyIso \}/, '')}
              </>
              : null
            }
          </p>
          {billingData.firstPayment
            ? <p className="share-page-v3__offer-promo-billing-note">
              {staticText.get('billedToday').replace(/\$\{ currencyIso \}/, billingData.firstPayment)}
            </p>
            : null
          }
        </div>
      </div>
      <p className="share-page-v3__offer-promo-cowntdown">
        {staticText.get('endsIn').replace(/\$\{ time \}/, '')}
        <CountdownTimer
          durationMs={durationMs}
          format={FORMAT_DAYS_HRS_MINS}
          staticText={staticText}
        />
      </p>
    </div>
  )
}

/**
 * The special offer component
 * @param {Map} props.staticText - The static text
 * @param {Map} props.offerPlan - The biling plan data
 * @param {Object} props.offerLink - The offer link url and query data
 * @param {String} props.expirationDate - The offer expiration date
 * @param {String} props.bgImageUrl - The promo view background image url
 * @param {Function} props.onOfferClick - The offer button click handler
 * @returns {JSX.Element} special offer component
 */
function SharePageV3SpecialOffer (props) {
  const {
    staticText,
    offerPlan,
    offerLink = {},
    expirationDate,
    displayType = OFFER_DISPLAY_TYPE_CARD,
    onOfferClick,
    language,
  } = props

  const durationMs = expirationDate ? new Date(expirationDate).getTime() - Date.now() : 0

  const billingData = useMemo(() => {
    const currencyIso = offerPlan.get('currencyIso')
    const monthlyPriceVal = offerPlan.getIn(['costs', '1'], '0')
    const firstPaymentVal = offerPlan.getIn(['costs', '0'], '0')
    const offerPriceVal = Math.floor((parseFloat(firstPaymentVal) / 3) * 100) / 100
    const monthlyPrice = formatPrice(currencyIso, monthlyPriceVal, false, null, language)
    const firstPayment = formatPrice(currencyIso, firstPaymentVal, false, null, language)
    const offerPrice = formatPrice(currencyIso, offerPriceVal, false, null, language)

    return {
      monthlyPrice,
      firstPayment,
      offerPrice,
    }
  }, [offerPlan])

  return displayType === OFFER_DISPLAY_TYPE_PROMO
    ? (<OfferPromo
      staticText={staticText}
      billingData={billingData}
      offerLink={offerLink}
      durationMs={durationMs}
      onOfferClick={onOfferClick}
      language={language}
    />)
    : (<OfferCard
      staticText={staticText}
      billingData={billingData}
      offerLink={offerLink}
      durationMs={durationMs}
      onOfferClick={onOfferClick}
    />)
}

export default SharePageV3SpecialOffer
